import { useCallback } from 'react';
import { debugLog } from './debug';

/**
 * Creates wrapped handlers that auto-save to custom view when active,
 * OR save to default view settings when on a default view.
 * 
 * @param {Object} params - Parameters for creating handlers
 * @returns {Object} Object containing all wrapped handlers
 */
export function useSettingHandlers({
  activeCustomView,
  updateView,
  isRestoringRef,
  // Default view settings (for saving when no custom view is active)
  updateDefaultViewSettings,
  currentViewType,
  // Setters
  setFiltersPerView,
  setTableColumnVisibility,
  setTodoElementVisibility,
  onFlowViewVisibilityChange,
  setGanttSwimlane,
  setShowArchived,
  setShowActionableOnly,
  setShowNestedCampaigns,
  setTodoViewMode,
  setTodoSwimlaneGroupBy,
  onFlowViewportChange,
  onGanttViewportChange,
}) {
  // Helper to save a setting to the active custom view
  const saveToCustomView = useCallback((settingKey, value) => {
    if (!activeCustomView || isRestoringRef.current || !updateView) {
      debugLog('saveToCustomView SKIPPED', {
        settingKey,
        hasActiveCustomView: !!activeCustomView,
        isRestoring: isRestoringRef.current,
        hasUpdateView: !!updateView,
      });
      return;
    }
    
    debugLog('saveToCustomView', settingKey, value, 'to view:', activeCustomView.name);
    
    const updatedSettings = {
      ...activeCustomView.settings,
      [settingKey]: value,
    };
    
    updateView(activeCustomView.id, { settings: updatedSettings });
  }, [activeCustomView, updateView, isRestoringRef]);

  // Helper to save a setting to the default view settings
  const saveToDefaultView = useCallback((viewType, settingKey, value) => {
    if (isRestoringRef.current || !updateDefaultViewSettings) {
      debugLog('saveToDefaultView SKIPPED', {
        settingKey,
        viewType,
        isRestoring: isRestoringRef.current,
        hasUpdateDefaultViewSettings: !!updateDefaultViewSettings,
      });
      return;
    }
    
    debugLog('saveToDefaultView', settingKey, value, 'for view type:', viewType);
    updateDefaultViewSettings(viewType, { [settingKey]: value });
  }, [updateDefaultViewSettings, isRestoringRef]);

  // Filter handler
  const handleFiltersChange = useCallback((view, newFilters) => {
    setFiltersPerView(prev => ({ ...prev, [view]: newFilters }));
    
    if (activeCustomView && view === activeCustomView.type && !isRestoringRef.current) {
      saveToCustomView('filters', newFilters);
    } else if (!activeCustomView && !isRestoringRef.current) {
      // Save to default view settings
      saveToDefaultView(view, 'filters', newFilters);
    }
  }, [setFiltersPerView, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Sort setting handler
  const handleSortSettingChange = useCallback((view, newSetting, setLocalSprintFn) => {
    debugLog('handleSortSettingChange called', { view, newSetting, hasSetLocalSprintFn: !!setLocalSprintFn, activeCustomView: activeCustomView?.name });
    
    if (setLocalSprintFn) {
      setLocalSprintFn(prev => {
        if (!prev) return prev;
        const currentPerView = prev.sortSettingsPerView || {};
        return { ...prev, sortSettingsPerView: { ...currentPerView, [view]: newSetting } };
      });
    }
    
    if (activeCustomView && view === activeCustomView.type && !isRestoringRef.current) {
      saveToCustomView('sortSetting', newSetting);
    }
  }, [activeCustomView, saveToCustomView, isRestoringRef]);

  // Card display settings handler
  const handleCardDisplaySettingsChange = useCallback((view, newSettings, setLocalSprintFn) => {
    setLocalSprintFn(prev => {
      if (!prev) return prev;
      const currentPerView = prev.cardDisplaySettings || {};
      return { ...prev, cardDisplaySettings: { ...currentPerView, [view]: newSettings } };
    });
    
    if (activeCustomView && view === activeCustomView.type && !isRestoringRef.current) {
      saveToCustomView('cardDisplaySettings', newSettings);
    }
  }, [activeCustomView, saveToCustomView, isRestoringRef]);

  // Swimlane setting handler (Kanban)
  const handleSwimlaneSettingChange = useCallback((newSetting, setLocalSprintFn) => {
    setLocalSprintFn(prev => prev ? { ...prev, swimlaneSetting: newSetting } : prev);
    
    if (activeCustomView && activeCustomView.type === 'kanban' && !isRestoringRef.current) {
      saveToCustomView('swimlaneSetting', newSetting);
    }
  }, [activeCustomView, saveToCustomView, isRestoringRef]);

  // Table column visibility handler
  const handleTableColumnVisibilityChange = useCallback((newVisibility) => {
    setTableColumnVisibility(newVisibility);
    
    if (activeCustomView && activeCustomView.type === 'table' && !isRestoringRef.current) {
      saveToCustomView('columnVisibility', newVisibility);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('table', 'columnVisibility', newVisibility);
    }
  }, [setTableColumnVisibility, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Todo element visibility handler
  const handleTodoElementVisibilityChange = useCallback((newVisibility) => {
    setTodoElementVisibility(newVisibility);
    
    if (activeCustomView && activeCustomView.type === 'todolist' && !isRestoringRef.current) {
      saveToCustomView('elementVisibility', newVisibility);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('todolist', 'elementVisibility', newVisibility);
    }
  }, [setTodoElementVisibility, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Flow view visibility handler
  const handleFlowViewVisibilityChange = useCallback((newVisibility) => {
    if (onFlowViewVisibilityChange) {
      onFlowViewVisibilityChange(newVisibility);
    }
    
    if (activeCustomView && activeCustomView.type === 'flow' && !isRestoringRef.current) {
      saveToCustomView('flowViewVisibility', newVisibility);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('flow', 'flowViewVisibility', newVisibility);
    }
  }, [onFlowViewVisibilityChange, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Gantt swimlane handler
  const handleGanttSwimlaneChange = useCallback((newSwimlane) => {
    if (setGanttSwimlane) {
      setGanttSwimlane(newSwimlane);
    }
    
    if (activeCustomView && activeCustomView.type === 'gantt' && !isRestoringRef.current) {
      saveToCustomView('ganttSwimlane', newSwimlane);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('gantt', 'ganttSwimlane', newSwimlane);
    }
  }, [setGanttSwimlane, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Toggle archived handler
  const handleToggleArchived = useCallback(() => {
    setShowArchived(prev => {
      const newValue = !prev;
      if (activeCustomView && !isRestoringRef.current) {
        saveToCustomView('showArchived', newValue);
      } else if (!activeCustomView && !isRestoringRef.current && currentViewType) {
        saveToDefaultView(currentViewType, 'showArchived', newValue);
      }
      return newValue;
    });
  }, [setShowArchived, activeCustomView, saveToCustomView, saveToDefaultView, currentViewType, isRestoringRef]);

  // Toggle actionable only handler
  const handleToggleActionableOnly = useCallback(() => {
    setShowActionableOnly(prev => {
      const newValue = !prev;
      if (activeCustomView && !isRestoringRef.current) {
        saveToCustomView('showActionableOnly', newValue);
      } else if (!activeCustomView && !isRestoringRef.current && currentViewType) {
        saveToDefaultView(currentViewType, 'showActionableOnly', newValue);
      }
      return newValue;
    });
  }, [setShowActionableOnly, activeCustomView, saveToCustomView, saveToDefaultView, currentViewType, isRestoringRef]);

  // Show nested campaigns handler
  const handleShowNestedCampaignsChange = useCallback((newValue) => {
    setShowNestedCampaigns(newValue);
    if (activeCustomView && !isRestoringRef.current) {
      saveToCustomView('showNestedCampaigns', newValue);
    } else if (!activeCustomView && !isRestoringRef.current && currentViewType) {
      saveToDefaultView(currentViewType, 'showNestedCampaigns', newValue);
    }
  }, [setShowNestedCampaigns, activeCustomView, saveToCustomView, saveToDefaultView, currentViewType, isRestoringRef]);

  // Todo view mode handler
  const handleTodoViewModeChange = useCallback((newMode) => {
    if (setTodoViewMode) {
      setTodoViewMode(newMode);
    }
    
    if (activeCustomView && activeCustomView.type === 'todolist' && !isRestoringRef.current) {
      saveToCustomView('viewMode', newMode);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('todolist', 'viewMode', newMode);
    }
  }, [setTodoViewMode, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Todo swimlane groupBy handler
  const handleTodoSwimlaneGroupByChange = useCallback((newGroupBy) => {
    if (setTodoSwimlaneGroupBy) {
      setTodoSwimlaneGroupBy(newGroupBy);
    }
    
    if (activeCustomView && activeCustomView.type === 'todolist' && !isRestoringRef.current) {
      saveToCustomView('swimlaneGroupBy', newGroupBy);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('todolist', 'swimlaneGroupBy', newGroupBy);
    }
  }, [setTodoSwimlaneGroupBy, activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  // Flow viewport handler (pan/zoom)
  const handleFlowViewportChange = useCallback((newViewport) => {
    if (onFlowViewportChange) {
      onFlowViewportChange(newViewport);
    }
    
    if (activeCustomView && activeCustomView.type === 'flow' && !isRestoringRef.current) {
      saveToCustomView('viewport', newViewport);
    }
  }, [onFlowViewportChange, activeCustomView, saveToCustomView, isRestoringRef]);

  // Gantt viewport handler (zoom level, scroll position)
  const handleGanttViewportChange = useCallback((newViewport) => {
    if (onGanttViewportChange) {
      onGanttViewportChange(newViewport);
    }
    
    if (activeCustomView && activeCustomView.type === 'gantt' && !isRestoringRef.current) {
      saveToCustomView('viewport', newViewport);
    }
  }, [onGanttViewportChange, activeCustomView, saveToCustomView, isRestoringRef]);

  // FOKUS mode handlers
  const handleFlowFokusChange = useCallback((isActive, taskId, isTransition, allFocusedIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: allFocusedIds ? Array.from(allFocusedIds) : [],
    };
    // This is called by FlowchartView when FOKUS mode changes
    if (activeCustomView && activeCustomView.type === 'flow' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('flow', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  const handleKanbanFokusChange = useCallback((isActive, focusedTaskIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : [],
    };
    if (activeCustomView && activeCustomView.type === 'kanban' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('kanban', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  const handleTableFokusChange = useCallback((isActive, focusedTaskIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : [],
    };
    if (activeCustomView && activeCustomView.type === 'table' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('table', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  const handleGanttFokusChange = useCallback((isActive, focusedTaskIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : [],
    };
    if (activeCustomView && activeCustomView.type === 'gantt' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('gantt', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  const handleTodoFokusChange = useCallback((isActive, focusedTaskIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : [],
    };
    if (activeCustomView && activeCustomView.type === 'todolist' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('todolist', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  const handleNeuralFokusChange = useCallback((isActive, focusedTaskIds) => {
    const fokusData = {
      isActive,
      focusedTaskIds: focusedTaskIds ? Array.from(focusedTaskIds) : [],
    };
    if (activeCustomView && activeCustomView.type === 'neural' && !isRestoringRef.current) {
      saveToCustomView('fokusMode', fokusData);
    } else if (!activeCustomView && !isRestoringRef.current) {
      saveToDefaultView('neural', 'fokusMode', fokusData);
    }
  }, [activeCustomView, saveToCustomView, saveToDefaultView, isRestoringRef]);

  return {
    handleFiltersChange,
    handleSortSettingChange,
    handleCardDisplaySettingsChange,
    handleSwimlaneSettingChange,
    handleTableColumnVisibilityChange,
    handleTodoElementVisibilityChange,
    handleFlowViewVisibilityChange,
    handleGanttSwimlaneChange,
    handleToggleArchived,
    handleToggleActionableOnly,
    handleShowNestedCampaignsChange,
    handleTodoViewModeChange,
    handleTodoSwimlaneGroupByChange,
    handleFlowViewportChange,
    handleGanttViewportChange,
    handleFlowFokusChange,
    handleKanbanFokusChange,
    handleTableFokusChange,
    handleGanttFokusChange,
    handleTodoFokusChange,
    handleNeuralFokusChange,
  };
}
